import React from 'react';
import { HiOutlinePencilSquare, HiOutlineBuildingLibrary } from "react-icons/hi2";
import { FaGraduationCap, FaLinkedin, FaGithub } from "react-icons/fa6";
import { SiLeetcode, SiCodeforces, SiCodechef } from "react-icons/si";
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import UserAvatar from '../UserAvatar/UserAvatar';
import { ROUTES } from '../../constants';

const DEFAULT_COLLEGE_NAME = "Your Institute";

const PersonalInfoCard = ({ userInfo }) => {
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();

  const isOwnProfile = user && userInfo?._id === user?._id;

  const collegeName = (userInfo?.collegeName || DEFAULT_COLLEGE_NAME).trim();
  const branch = userInfo?.branch || "";
  const graduationYear = userInfo?.graduationYear || "";

  const SocialLink = ({ icon: Icon, link, color }) => {
    if (!link || typeof link !== "string") return null;

    const trimmed = link.trim();
    if (!trimmed) return null;

    const domain = trimmed.replace(/https?:\/\/(www\.)?/, '').split('/')[0];

    return (
      <button
        type="button"
        onClick={() => window.open(trimmed, '_blank', 'noopener,noreferrer')}
        className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 hover:bg-white border border-transparent hover:border-slate-200 transition-all group w-full"
      >
        <span className={`${color} group-hover:scale-110 transition-transform`}>
          <Icon size={20} />
        </span>
        <span className="text-xs font-bold text-slate-600 truncate uppercase tracking-tighter">
          {domain}
        </span>
      </button>
    );
  };

  return (
    <div className="p-6 flex flex-col items-center h-full">
      {/* Profile Header */}
      <div className="flex flex-col items-center mb-8">
        <UserAvatar
          src={userInfo?.profileImage}
          name={userInfo?.name}
          size={100}
          className="ring-2 ring-slate-100"
        />

        <div className="mt-4 text-center">
          <h1 className="font-black text-2xl text-slate-900 tracking-tight">
            {userInfo?.name || "User"}
          </h1>
          <p className="text-sm text-slate-400 font-medium">
            {userInfo?.email || ""}
          </p>
        </div>

        {isOwnProfile && (
          <button
            onClick={() => navigate(ROUTES.editProfile)}
            className="mt-6 flex items-center gap-2 bg-slate-900 text-white px-8 py-3 rounded-2xl text-sm font-black hover:bg-blue-600 transition-all active:scale-95 shadow-lg shadow-blue-100"
          >
            <HiOutlinePencilSquare size={18} />
            Edit Profile
          </button>
        )}
      </div>

      {/* Academic Info */}
      <div className="w-full space-y-3 mb-8">
        {/* Institute */}
        <div className="flex items-center gap-3 p-4 bg-blue-50/50 rounded-2xl border border-blue-100/50">
          <HiOutlineBuildingLibrary className="text-blue-600" size={22} />
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase text-blue-400 tracking-widest">
              Institute
            </p>
            <p className="text-sm font-bold text-slate-700 truncate">
              {collegeName}
            </p>
          </div>
        </div>

        {/* Degree */}
        <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
          <FaGraduationCap className="text-slate-400" size={22} />
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">
              Degree
            </p>
            <p className="text-sm font-bold text-slate-700 truncate">
              {(branch || graduationYear) ? `${branch}${branch && graduationYear ? " • " : ""}${graduationYear}` : "—"}
            </p>
          </div>
        </div>
      </div>

      {/* Social/Coding Links */}
      <div className="w-full grid grid-cols-1 gap-2">
        <p className="text-[10px] font-black uppercase text-slate-300 tracking-[0.2em] mb-2 ml-1">
          Professional Links
        </p>
        <SocialLink icon={FaLinkedin} link={userInfo?.linkedinLink} color="text-[#0077b5]" />
        <SocialLink icon={FaGithub} link={userInfo?.githubLink} color="text-[#333]" />
        <SocialLink icon={SiLeetcode} link={userInfo?.leetcodeLink} color="text-[#FFA116]" />
        <SocialLink icon={SiCodeforces} link={userInfo?.codeforcesLink} color="text-[#1F8ACB]" />
        <SocialLink icon={SiCodechef} link={userInfo?.codechefLink} color="text-[#5B4638]" />
      </div>
    </div>
  );
};

export default PersonalInfoCard;